import React, {
  ChangeEvent,
  FC,
  FormEvent,
  KeyboardEvent,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';

import { Conversation, ResourceToAdd } from '../../../models/Chat';
import { CurrentUser } from '../../../rematch-models/user';
import { chatService } from '../../../config/rootService';
import { Dispatch } from '../../../config/Store';
import { getActiveConversation } from '../../../selectors/chatSelectors';
import { resourcesToAddSelector } from '../../../selectors/resourcesSelectors';
import { getCurrentUser } from '../../../selectors/userSelectors';
import ChatResourceDrawer from '../ChatResourceDrawer';
import ChatInputFormSubmitButton from './SubmitButton/SubmitButton';
import ChatInputFormTextField from './TextField/TextField';
import EmojiFormWidget from './EmojiFormWidget/EmojiFormWidget';
import ResourcesButton from './ResourcesButton/ResourcesButton';
import usePrevious from '../../../hooks/usePrevious';

const styles = (theme) =>
  createStyles({
    chatForm: {
      background: '#ffffff',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
      paddingBottom: 12,
    },
  });

const ChatInputForm: FC<WithStyles> = ({ classes }) => {
  const activeConversation: Conversation | null = useSelector(getActiveConversation);
  const previousActiveConversation = usePrevious(activeConversation);

  const resourcesToAdd: ResourceToAdd[] = useSelector(resourcesToAddSelector);
  const coach: CurrentUser = useSelector(getCurrentUser);

  const [chatMessage, setChatMessage] = useState<string>('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<EventTarget | null>(null);

  const dispatch = useDispatch<Dispatch>();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: FormEvent | KeyboardEvent) => {
    e.preventDefault();
    const oldChatMessage = chatMessage;

    if ((!oldChatMessage && resourcesToAdd.length === 0) || !activeConversation) {
      return;
    }

    const result = await chatService.createMessage({
      chatMessage,
      resourcesToAdd,
      conversationUuid: activeConversation.uuid,
      senderUuid: coach.uuid,
      receiverUuid: activeConversation.member.uuid,
    });

    dispatch.chat.setRead(activeConversation.uuid);

    if (result) {
      dispatch.chat.setMessageNotNewMatch();
      setChatMessage('');
      dispatch.resources.reset();
    } else {
      // if update fails, add back chat message to state so user can try to send again
      setChatMessage(oldChatMessage);
      dispatch.resources.setResources(resourcesToAdd);
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if ((e.key === 'Enter' || e.keyCode === 13) && !e.shiftKey) {
      handleSubmit(e);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => setChatMessage(event.target.value);

  const toggleDrawer = (event: SyntheticEvent) => {
    !isDrawerOpen ? setAnchorEl(event.currentTarget) : setAnchorEl(null);
    setIsDrawerOpen(!isDrawerOpen);
  };

  const conversationHasChanged =
    activeConversation && activeConversation.uuid !== previousActiveConversation?.uuid;

  useEffect(() => {
    if (!conversationHasChanged) return;

    if (inputRef && inputRef.current) {
      inputRef.current?.focus();
    }
  }, [conversationHasChanged]);

  useEffect(() => {
    if (!conversationHasChanged) return;

    setChatMessage('');
  }, [conversationHasChanged, setChatMessage]);

  return (
    <form onSubmit={handleSubmit} className={classes.chatForm}>
      <ChatInputFormTextField
        chatMessage={chatMessage}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        ref={inputRef}
        resourcesToAdd={resourcesToAdd}
      />

      <EmojiFormWidget onChange={({ native }) => setChatMessage(`${chatMessage}${native}`)} />

      <ResourcesButton onClick={toggleDrawer} />

      <ChatInputFormSubmitButton isDisabled={!chatMessage && !resourcesToAdd.length} />

      <ChatResourceDrawer isOpen={isDrawerOpen} anchorEl={anchorEl} onClose={toggleDrawer} />
    </form>
  );
};

export default withStyles(styles)(ChatInputForm);
